import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang, tr } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Info, Tag, Check, Copy } from "lucide-react";

export default function Checkout() {
  const { t } = useLang();
  const { user } = useAuth();
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    pickup_at_club: true, shipping_address: "",
    payment_method: "cash",
  });
  const [promo, setPromo] = useState({ code: "", applied: null, error: "", loading: false });
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [bankInfo, setBankInfo] = useState(null);
  const [siteBank, setSiteBank] = useState(null); // {iban, bic, bank_holder} loaded from public site-settings
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then(({ data }) => setSiteBank({
      // Strip a redundant "IBAN :" prefix if a stale stored value contains it
      iban: (data.iban || "").replace(/^\s*iban\s*:\s*/i, ""),
      bic: data.bic || "",
      holder: data.bank_holder || "",
    })).catch(() => {});
  }, []);

  const isMember = user && user !== false;
  const discount = promo.applied?.discount || 0;
  const finalTotal = Math.max(0, total - discount);

  if (items.length === 0) {
    return (
      <div className="pt-32 pb-24 text-center" data-testid="checkout-empty">
        <p className="text-slate-500">{t.shop.cartEmpty}</p>
        <Link to="/shop" className="inline-block mt-6 bg-navy text-white px-8 py-3 text-xs uppercase tracking-[0.25em]">{t.shop.backToShop}</Link>
      </div>
    );
  }

  const applyPromo = async (e) => {
    e.preventDefault();
    if (!promo.code.trim()) return;
    setPromo((p) => ({ ...p, loading: true, error: "" }));
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code: promo.code.trim(), subtotal: total,
      });
      setPromo({ code: data.code, applied: data, error: "", loading: false });
    } catch (e) {
      setPromo((p) => ({ ...p, applied: null, error: e.response?.data?.detail || "Code invalide", loading: false }));
    }
  };

  const removePromo = () => setPromo({ code: "", applied: null, error: "", loading: false });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        promo_code: promo.applied?.code || "",
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, unit_price: i.unit_price,
          quantity: i.quantity, size: i.size, color: i.color,
          participant_name: i.participant_name || "",
          participant_notes: i.participant_notes || "",
          // Extended stage-booking fields (RYCM iter 21+)
          participant_first_name: i.participant_first_name || "",
          participant_last_name:  i.participant_last_name  || "",
          participant_birthdate:  i.participant_birthdate  || "",
          participant_email:      i.participant_email      || "",
          participant_phone:      i.participant_phone      || "",
          participant_address:    i.participant_address    || "",
          participant_weight:     i.participant_weight     || "",
          participant_height:     i.participant_height     || "",
          participant_boat:       i.participant_boat       || "",
          participant_experience: i.participant_experience || "",
        })),
      };
      const { data } = await api.post("/checkout", payload);
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      } else if (data.payment_method === "bank_transfer" && data.bank_info) {
        clear();
        setBankInfo(data.bank_info);
        setInfo(data.message);
        // Auto-navigate after 8s so user can copy bank details
        setTimeout(() => navigate(`/checkout/complete?order_id=${data.order_id}`), 8000);
      } else {
        clear();
        setInfo(data.message || t.shop.noOnlinePayment);
        setTimeout(() => navigate(`/checkout/complete?order_id=${data.order_id}`), 2500);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28 pb-24 bg-sand min-h-screen" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12">
        <h1 className="font-serif text-4xl text-navy">{t.shop.checkoutTitle}</h1>
        <div className="divider-gold mt-5 mb-10" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <form onSubmit={submit} className="lg:col-span-7 space-y-6" data-testid="checkout-form">
            <div>
              <div className="label-eyebrow mb-4">{t.shop.contactInfo}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t.shop.name} value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} required testid="checkout-name" />
                <Field label={t.shop.email} type="email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} required testid="checkout-email" />
                <Field label={t.shop.phone} value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} testid="checkout-phone" />
              </div>
            </div>

            <div>
              <div className="label-eyebrow mb-4">{t.shop.pickupTitle}</div>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-4 bg-white border border-slate-200 cursor-pointer hover:border-navy" data-testid="pickup-club">
                  <input type="radio" checked={form.pickup_at_club} onChange={() => setForm({ ...form, pickup_at_club: true })} />
                  <span className="text-sm text-navy">{t.shop.pickup}</span>
                </label>
                <label className="flex items-center gap-3 p-4 bg-white border border-slate-200 cursor-pointer hover:border-navy" data-testid="pickup-ship">
                  <input type="radio" checked={!form.pickup_at_club} onChange={() => setForm({ ...form, pickup_at_club: false })} />
                  <span className="text-sm text-navy">{t.shop.shipping}</span>
                </label>
              </div>
              {!form.pickup_at_club && (
                <div className="mt-4">
                  <Field label={t.shop.address} value={form.shipping_address} onChange={(v) => setForm({ ...form, shipping_address: v })} required testid="checkout-shipping" />
                </div>
              )}
            </div>

            {/* Payment method */}
            <div data-testid="payment-method">
              <div className="label-eyebrow mb-4">{tr("Méthode de paiement")}</div>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-4 bg-white border cursor-pointer ${form.payment_method === "cash" ? "border-club-red" : "border-slate-200"}`} data-testid="pay-cash">
                  <input type="radio" name="pm" checked={form.payment_method === "cash"} onChange={() => setForm({ ...form, payment_method: "cash" })} className="mt-1" />
                  <div>
                    <div className="text-sm text-navy font-medium">{tr("Espèces au club-house")}</div>
                    <div className="text-xs text-slate-500 mt-1">{tr("Paiement lors du retrait de votre commande sur place.")}</div>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-4 bg-white border cursor-pointer ${form.payment_method === "bank_transfer" ? "border-club-red" : "border-slate-200"}`} data-testid="pay-transfer">
                  <input type="radio" name="pm" checked={form.payment_method === "bank_transfer"} onChange={() => setForm({ ...form, payment_method: "bank_transfer" })} className="mt-1" />
                  <div>
                    <div className="text-sm text-navy font-medium">Virement bancaire</div>
                    <div className="text-xs text-slate-500 mt-1">{tr("Effectuez le virement avec la communication fournie après la confirmation.")}</div>
                  </div>
                </label>
              </div>
              {/* Bank coordinates preview (RYCM iter 28) — visible as soon as bank_transfer is selected,
                  so the client can prepare the transfer immediately. */}
              {form.payment_method === "bank_transfer" && siteBank && (siteBank.iban || siteBank.bic) && (
                <div className="mt-3 bg-sand border border-club-yellow p-5" data-testid="bank-preview">
                  <div className="flex items-center justify-between mb-3">
                    <div className="label-eyebrow text-club-yellow-dark">{tr("Coordonnées bancaires RYCM")}</div>
                    <button type="button"
                      onClick={() => { try { navigator.clipboard.writeText(siteBank.iban); } catch (e) {} }}
                      className="text-[10px] uppercase tracking-widest text-navy hover:text-club-red flex items-center gap-1"
                      data-testid="bank-preview-copy">
                      <Copy className="w-3 h-3" /> Copier l'IBAN
                    </button>
                  </div>
                  <ul className="space-y-1.5 text-sm font-mono">
                    <li><span className="text-slate-500">IBAN :</span> <span className="text-navy font-bold" data-testid="bank-preview-iban">{siteBank.iban || "—"}</span></li>
                    {siteBank.bic && <li><span className="text-slate-500">BIC :</span> <span className="text-navy">{siteBank.bic}</span></li>}
                    {siteBank.holder && <li><span className="text-slate-500">Titulaire :</span> <span className="text-navy">{siteBank.holder}</span></li>}
                  </ul>
                  <p className="text-[11px] text-slate-600 mt-3 italic">
                    Une communication structurée unique vous sera attribuée après confirmation —
                    ne payez qu'avec cette communication pour que le trésorier identifie votre paiement.
                  </p>
                </div>
              )}
            </div>

            {/* Promo code (members only) */}
            {isMember && (
              <div data-testid="promo-section">
                <div className="label-eyebrow mb-4 flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> Code promo membre</div>
                {!promo.applied ? (
                  <div className="flex gap-2">
                    <input
                      placeholder="MEMBRE2026" value={promo.code}
                      onChange={(e) => setPromo({ ...promo, code: e.target.value.toUpperCase() })}
                      className="flex-1 border border-slate-300 px-3 py-2.5 bg-white uppercase tracking-widest"
                      data-testid="promo-input"
                    />
                    <button type="button" onClick={applyPromo} disabled={promo.loading || !promo.code.trim()}
                      className="px-5 py-2.5 bg-navy text-white text-xs uppercase tracking-[0.2em] hover:bg-club-red disabled:opacity-60" data-testid="promo-apply">
                      {promo.loading ? "…" : "Appliquer"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-300 p-3" data-testid="promo-applied">
                    <div className="flex items-center gap-2 text-sm text-green-800">
                      <Check className="w-4 h-4" />{tr("Code")}<b>{promo.applied.code}</b> appliqué — économie € {promo.applied.discount.toFixed(2)}
                    </div>
                    <button type="button" onClick={removePromo} className="text-xs text-red-600 hover:underline" data-testid="promo-remove">Retirer</button>
                  </div>
                )}
                {promo.error && <div className="text-sm text-red-600 mt-2" data-testid="promo-error">{promo.error}</div>}
              </div>
            )}
            {!isMember && (
              <div className="text-xs text-slate-500 italic" data-testid="promo-login-hint">
                <Link to="/login" className="underline">Connectez-vous</Link> pour utiliser un code promo membre.
              </div>
            )}

            {err && <div className="text-sm text-red-600" data-testid="checkout-error">{err}</div>}
            {info && (
              <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-300 text-yellow-900 p-4 text-sm" data-testid="checkout-info">
                <Info className="w-5 h-5 shrink-0 mt-0.5" /> <div>{info}</div>
              </div>
            )}
            {bankInfo && (
              <div className="bg-navy text-white p-6" data-testid="bank-info">
                <div className="label-eyebrow text-club-yellow mb-3">{tr("Coordonnées de virement")}</div>
                <ul className="space-y-2 text-sm font-mono">
                  <li><span className="text-slate-400">IBAN :</span> <span className="text-club-yellow" data-testid="bank-iban">{bankInfo.iban || "—"}</span></li>
                  <li><span className="text-slate-400">BIC :</span> {bankInfo.bic || "—"}</li>
                  <li><span className="text-slate-400">Titulaire :</span> {bankInfo.holder || "—"}</li>
                  <li><span className="text-slate-400">Communication :</span> <span className="text-club-yellow font-bold" data-testid="bank-reference">{bankInfo.reference}</span></li>
                </ul>
                <p className="text-xs text-slate-300 mt-4">{tr("Veuillez indiquer la communication ci-dessus pour identifier votre paiement.")}</p>
              </div>
            )}

            <button type="submit" disabled={submitting} className="w-full bg-club-red text-white py-4 text-xs uppercase tracking-[0.25em] hover:bg-club-red-dark transition-colors disabled:opacity-60" data-testid="checkout-submit">
              {submitting ? "…" : (form.payment_method === "bank_transfer" ? tr("Confirmer la commande (virement)") : tr("Confirmer la commande (paiement au club)"))}
            </button>
          </form>

          <aside className="lg:col-span-5">
            <div className="bg-white border border-slate-200 p-6">
              <div className="font-serif text-2xl text-navy mb-4">{t.nav.cart}</div>
              <div className="divider-gold mb-5" />
              <div className="space-y-3">
                {items.map((i) => (
                  <div key={`${i.product_id}-${i.size}-${i.color}`} className="flex justify-between text-sm">
                    <div>
                      <div className="text-navy">{i.name} <span className="text-slate-400">× {i.quantity}</span></div>
                      {(i.size || i.color) && <div className="text-xs text-slate-500">{i.size} · {i.color}</div>}
                    </div>
                    <div className="text-navy">€ {(i.unit_price * i.quantity).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              {promo.applied && (
                <div className="border-t border-slate-200 mt-5 pt-3 space-y-1.5" data-testid="checkout-summary-discount">
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>Sous-total</span><span>€ {total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-club-red">
                    <span>Remise ({promo.applied.code})</span><span>− € {promo.applied.discount.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <div className="border-t border-slate-200 mt-5 pt-5 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">{t.shop.total}</span>
                <span className="font-serif text-3xl text-navy" data-testid="checkout-total">€ {finalTotal.toFixed(2)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2.5 bg-white" data-testid={testid} />
    </div>
  );
}
